import React, { useState, useEffect } from 'react';
import { Calendar, Gift, Heart, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Contact {
  id: string;
  name: string;
  birthday: string | null;
  anniversary: string | null;
  relationship: string;
  card_preference: string;
  gender: 'male' | 'female' | null;
}

interface UpcomingEvent {
  id: string;
  contactId: string;
  contactName: string;
  date: string;
  occasion: 'Birthday' | 'Anniversary';
  relationship: string;
  card_preference: string;
  gender: 'male' | 'female' | null;
}

interface UpcomingEventsProps {
  onSelectEvent: (event: UpcomingEvent) => void;
}

export function UpcomingEvents({ onSelectEvent }: UpcomingEventsProps) {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  async function fetchUpcomingEvents() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get current date as YYYY-MM-DD
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all contacts for this user
      const { data: contacts, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('user_id', session.user.id);

      if (error) throw error;

      // Process contacts into events
      const upcomingEvents: UpcomingEvent[] = [];
      contacts.forEach(contact => {
        if (contact.birthday) {
          // Convert birthday to this year's date
          const now = new Date();
          const birthDate = new Date(contact.birthday);
          const thisYearBirthday = new Date(now.getFullYear(), birthDate.getMonth(), birthDate.getDate());
          
          // If this year's birthday has passed, use next year
          if (thisYearBirthday < now) {
            thisYearBirthday.setFullYear(now.getFullYear() + 1);
          }
          
          // Check if within the next 30 days
          const thirtyDaysFromNow = new Date(now);
          thirtyDaysFromNow.setDate(now.getDate() + 30);
          
          if (thisYearBirthday <= thirtyDaysFromNow) {
            upcomingEvents.push({
              id: `birthday-${contact.id}`,
              contactId: contact.id,
              contactName: contact.name,
              date: thisYearBirthday.toISOString(),
              occasion: 'Birthday',
              relationship: contact.relationship,
              card_preference: contact.card_preference,
              gender: contact.gender
            });
          }
        }

        if (contact.anniversary) {
          // Convert anniversary to this year's date
          const now = new Date();
          const anniversaryDate = new Date(contact.anniversary);
          const thisYearAnniversary = new Date(now.getFullYear(), anniversaryDate.getMonth(), anniversaryDate.getDate());
          
          // If this year's anniversary has passed, use next year
          if (thisYearAnniversary < now) {
            thisYearAnniversary.setFullYear(now.getFullYear() + 1);
          }
          
          // Check if within the next 30 days
          const thirtyDaysFromNow = new Date(now);
          thirtyDaysFromNow.setDate(now.getDate() + 30);
          
          if (thisYearAnniversary <= thirtyDaysFromNow) {
            upcomingEvents.push({
              id: `anniversary-${contact.id}`,
              contactId: contact.id,
              contactName: contact.name,
              date: thisYearAnniversary.toISOString(),
              occasion: 'Anniversary',
              relationship: contact.relationship,
              card_preference: contact.card_preference,
              gender: contact.gender
            });
          }
        }
      });

      // Sort events by date
      upcomingEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {events.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-gray-500">No upcoming birthdays or anniversaries in the next 30 days.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((event) => (
            <div
              key={event.id}
              className="flex items-center justify-between p-4 bg-white rounded-lg border hover:border-brand-primary transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                  {event.occasion === 'Birthday' ? (
                    <Gift className="w-5 h-5 text-brand-primary" />
                  ) : (
                    <Heart className="w-5 h-5 text-brand-primary" />
                  )}
                </div>
                <div>
                  <div className="font-medium text-brand-secondary">{event.contactName}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(event.date).toLocaleDateString()} • {event.occasion}
                  </div>
                  <div className="text-xs text-gray-400">
                    {event.relationship} • Prefers {event.card_preference} cards
                  </div>
                </div>
              </div>
              <button
                onClick={() => onSelectEvent(event)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              >
                <Send className="w-4 h-4" />
                Send Card
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 